import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Alert,
  Paper,
  Autocomplete,
  TextField
} from '@mui/material';
import { BarChart, TrendingUp, CalendarToday, Business } from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import apiService from '../services/apiService';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const SessionStatisticsChart = ({ organizationId: propOrganizationId, organizationName: propOrganizationName, hideOrganizationSelector = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('month');
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(propOrganizationId || null);
  const [selectedOrganizationName, setSelectedOrganizationName] = useState(propOrganizationName || 'All Organizations');
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);

  // Fetch organizations for dropdown
  const fetchOrganizations = useCallback(async () => {
    setLoadingOrganizations(true);
    try {
      const result = await apiService.fetchOrganizations();
      if (result.success) {
        setOrganizations(result.data.organizations || []);
      }
    } catch (err) {
      console.error('Error fetching organizations:', err);
    }
    setLoadingOrganizations(false);
  }, []);

  // Load organizations on component mount
  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const fetchSessionStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiService.fetchOrganizationSessionStats(selectedOrganizationId, period);
      
      if (result.success) {
        setData(result.data);
      } else {
        setError(typeof result.error === 'string' ? result.error : 'Failed to load session statistics');
      }
    } catch (err) {
      console.error('Error fetching session statistics:', err);
      setError('Failed to load session statistics');
    }
    
    setLoading(false);
  }, [selectedOrganizationId, period]);

  useEffect(() => {
    fetchSessionStats();
  }, [fetchSessionStats]);

  const handlePeriodChange = (event, newPeriod) => {
    if (newPeriod !== null) {
      setPeriod(newPeriod);
    }
  };

  const handleOrganizationChange = (event, newValue) => {
    if (newValue) {
      setSelectedOrganizationId(newValue.unique_id);
      setSelectedOrganizationName(newValue.organization_name);
    } else {
      setSelectedOrganizationId(null);
      setSelectedOrganizationName('All Projects');
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      const periodLabels = {
        'week': 'MMM DD',
        'month': 'MMM DD',
        'year': 'MMM YYYY'
      };
      
      const format = periodLabels[period] || 'MMM DD';
      
      if (format === 'MMM DD') {
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        });
      } else if (format === 'MMM YYYY') {
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          year: 'numeric' 
        });
      }
      
      return date.toLocaleDateString();
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return 'Invalid Date';
    }
  };

  const getChartData = () => {
    try {
      if (!data || !data.chart_data || !data.chart_data.labels || !data.chart_data.datasets) {
        return null;
      }

      // Ensure labels and data are arrays
      const labels = Array.isArray(data.chart_data.labels) ? data.chart_data.labels : [];
      const datasets = Array.isArray(data.chart_data.datasets) ? data.chart_data.datasets : [];
      
      if (datasets.length === 0 || !datasets[0] || !Array.isArray(datasets[0].data)) {
        return null;
      }

      return {
        labels: labels.map(label => {
          const formattedLabel = formatDate(label);
          return typeof formattedLabel === 'string' ? formattedLabel : 'Invalid Date';
        }),
        datasets: [
          {
            label: 'Session Count',
            data: datasets[0].data.map(value => {
              const numValue = Number(value);
              return isNaN(numValue) ? 0 : numValue;
            }),
            borderColor: 'rgb(75, 192, 192)',
            backgroundColor: 'rgba(75, 192, 192, 0.1)',
            borderWidth: 2,
            tension: 0.4,
            fill: true,
            pointBackgroundColor: 'rgb(75, 192, 192)',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
          }
        ]
      };
    } catch (error) {
      console.error('Error processing chart data:', error);
      return null;
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(75, 192, 192, 0.5)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: (context) => {
            return `Date: ${context[0].label}`;
          },
          label: (context) => {
            return `Sessions: ${context.parsed.y}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#666',
          font: {
            size: 12
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
          drawBorder: false
        },
        ticks: {
          color: '#666',
          font: {
            size: 12
          },
          callback: function(value) {
            return value.toLocaleString();
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };

  const getPeriodLabel = (period) => {
    switch (period) {
      case 'week':
        return 'Week';
      case 'month':
        return 'Month';
      case 'year':
        return 'Year';
      default:
        return 'Month';
    }
  };

  const chartData = getChartData();

  // Always render the main structure, only change the content area
  return (
    <Card sx={{ mb: 4, width: '100%' }}>
      <CardContent>
        {/* Header with Title, Organization Selector, and Period Selector */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BarChart sx={{ color: 'primary.main' }} />
            <Typography variant="h6" fontWeight={600}>
              Session Statistics
            </Typography>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            {/* Organization Selector - Only show if not hidden */}
            {!hideOrganizationSelector && (
              <Autocomplete
                options={[{ unique_id: null, organization_name: 'All Projects' }, ...organizations]}
                getOptionLabel={(option) => option.organization_name || ''}
                value={selectedOrganizationId ? organizations.find(org => org.unique_id === selectedOrganizationId) : { unique_id: null, organization_name: 'All Projects' }}
                onChange={handleOrganizationChange}
                filterOptions={(options, { inputValue }) => {
                  const filtered = options.filter(option => 
                    option.organization_name && 
                    option.organization_name.toLowerCase().includes(inputValue.toLowerCase())
                  );
                  return filtered;
                }}
                                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Project"
                      size="small"
                      sx={{ minWidth: 280 }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <Business sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                        )
                      }}
                    />
                  )}
                  loading={loadingOrganizations}
                  disabled={loading}
                  sx={{ minWidth: 280 }}
              />
            )}
            
            {/* Period Selector */}
            <ToggleButtonGroup
              value={period}
              exclusive
              onChange={handlePeriodChange}
              size="small"
              disabled={loading}
              sx={{
                '& .MuiToggleButton-root': {
                  px: 2,
                  py: 0.5,
                  fontSize: '0.875rem',
                  fontWeight: 500
                }
              }}
            >
              <ToggleButton value="week">
                <CalendarToday sx={{ mr: 0.5, fontSize: 16 }} />
                Week
              </ToggleButton>
              <ToggleButton value="month">
                <CalendarToday sx={{ mr: 0.5, fontSize: 16 }} />
                Month
              </ToggleButton>
              <ToggleButton value="year">
                <CalendarToday sx={{ mr: 0.5, fontSize: 16 }} />
                Year
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Content Area - Fixed Height Container */}
        <Box sx={{ 
          minHeight: 500, // Fixed height to prevent layout shifts
          display: 'flex',
          flexDirection: 'column'
        }}>
          {loading ? (
            // Loading State
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              flex: 1,
              py: 8
            }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Loading session statistics...
              </Typography>
            </Box>
          ) : error ? (
            // Error State
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flex: 1,
              py: 4
            }}>
              <Alert severity="error">
                <Typography variant="body2">
                  {typeof error === 'string' ? error : 'An error occurred while loading session statistics'}
                </Typography>
              </Alert>
            </Box>
          ) : !data ? (
            // No Data State
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flex: 1,
              py: 4
            }}>
              <Alert severity="info">
                <Typography variant="body2">
                  No session data available for this project.
                </Typography>
              </Alert>
            </Box>
          ) : (
            // Success State - Chart and Statistics
            <>
              {/* Statistics Summary */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  mb: 3, 
                  bgcolor: 'grey.50', 
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 2
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-around',
                  flexWrap: 'wrap',
                  gap: 2
                }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={700} color="primary.main">
                      {typeof data.total_sessions === 'number' ? data.total_sessions.toLocaleString() : '0'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Sessions
                    </Typography>
                  </Box>
                  
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" fontWeight={600} color="text.primary">
                      {typeof data.period === 'string' ? data.period : getPeriodLabel(period)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Period
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Chart Container */}
              <Box sx={{ 
                position: 'relative',
                height: 400,
                width: '100%',
                flex: 1
              }}>
                {chartData && chartData.labels && chartData.datasets ? (
                  <Line data={chartData} options={chartOptions} />
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: '100%',
                    color: 'text.secondary'
                  }}>
                    <Typography variant="body2">
                      No chart data available
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Chart Footer */}
              <Box sx={{ 
                mt: 2, 
                pt: 2, 
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1
              }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {getPeriodLabel(period).toLowerCase()}ly session data for{' '}
                  <strong>{selectedOrganizationName}</strong>
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUp sx={{ fontSize: 16, color: 'primary.main' }} />
                  <Typography variant="body2" color="text.secondary">
                    Last updated: {new Date().toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default SessionStatisticsChart; 